import React from 'react'
import { Routes, Route, BrowserRouter, Navigate } from 'react-router-dom'
import { AdminQueryFlagsBootstrap } from 'src/components/AdminQueryFlagsBootstrap'
import { FirstPermittedRedirect } from 'src/components/auth/FirstPermittedRedirect'
import { RequireInternalAuth } from 'src/components/auth/RequireInternalAuth'
import { RequirePermission } from 'src/components/auth/RequirePermission'
import { WorkosAuthProvider } from 'src/components/auth/WorkosAuthProvider'
import { AdminShell } from 'src/components/layout/AdminShell'
import { QueryPreservingNavigate } from 'src/components/layout/QueryPreservingNavigate'
import { NotFoundPage } from 'src/pages/NotFoundPage'
import { LoginPage } from 'src/pages/auth/LoginPage'
import { LauncherPage } from 'src/pages/LauncherPage'
import { AppPlaceholderPage } from 'src/pages/AppPlaceholderPage'
import { DocumentReviewQueuePage } from 'src/pages/internal-document-review/DocumentReviewQueuePage'
import { ContractorReviewPage } from 'src/pages/internal-document-review/ContractorReviewPage'
import { McReviewPage } from 'src/pages/internal-document-review/McReviewPage'
import { PartnerDriverPage } from 'src/pages/update-partner-driver/PartnerDriverPage'
import { PartnerDriverSearchPage } from 'src/pages/update-partner-driver/PartnerDriverSearchPage'
import { PartnerDriverStepsPage } from 'src/pages/update-partner-driver/PartnerDriverStepsPage'
import { ScheduledActionDetailPage } from 'src/pages/schedulers-management/ScheduledActionDetailPage'
import { ScheduledActionRetriesPage } from 'src/pages/schedulers-management/ScheduledActionRetriesPage'
import { SchedulersManagementPage } from 'src/pages/schedulers-management/SchedulersManagementPage'
import { InternalRolePage } from 'src/pages/users-management/InternalRolePage'
import { UsersManagementPage } from 'src/pages/users-management/UsersManagementPage'
import { BankOnboardingPage } from 'src/pages/payments-ops/BankOnboardingPage'
import { NewBankOnboardingPage } from 'src/pages/payments-ops/NewBankOnboardingPage'
import { IncomingTransactionsPage } from 'src/pages/payments-ops/IncomingTransactionsPage'
import { KillswitchPage } from 'src/pages/payments-ops/KillswitchPage'
import { OutgoingTransactionsPage } from 'src/pages/payments-ops/OutgoingTransactionsPage'
import { PaymentsOpsLayout } from 'src/pages/payments-ops/PaymentsOpsLayout'
import { PaymentsReviewPage } from 'src/pages/payments-ops/PaymentsReviewPage'
import { RefundsPage } from 'src/pages/payments-ops/RefundsPage'
import { UsBankTransactionsPage } from 'src/pages/payments-ops/UsBankTransactionsPage'
import { ReconciliationPage } from 'src/pages/payments-ops/ReconciliationPage'
import { WalletsPage } from 'src/pages/payments-ops/WalletsPage'
import { FeesWithdrawReportsPage } from 'src/pages/payments-ops/FeesWithdrawReportsPage'
import { PAYMENTS_OPS_ITEMS } from 'src/pages/payments-ops/paymentsOpsNav'
import { RemoteConfigKeysPage } from 'src/pages/remote-config/RemoteConfigKeysPage'
import { RemoteConfigLayout } from 'src/pages/remote-config/RemoteConfigLayout'
import { REMOTE_CONFIG_ITEMS } from 'src/pages/remote-config/remoteConfigNav'
import type { PaymentsOpsSlug } from 'src/pages/payments-ops/paymentsOpsNav.types'
import { AdminDashboardLayout } from 'src/pages/admin-dashboard/AdminDashboardLayout'
import { CustomersPage } from 'src/pages/admin-dashboard/customers/CustomersPage'
import { CustomerLayout } from 'src/pages/admin-dashboard/customers/customer/CustomerLayout'
import { CustomerOverviewPage } from 'src/pages/admin-dashboard/customers/customer/CustomerOverviewPage'
import { CustomerSettingsPage } from 'src/pages/admin-dashboard/customers/customer/CustomerSettingsPage'
import { CustomerBusinessPage } from 'src/pages/admin-dashboard/customers/customer/business/CustomerBusinessPage'
import { CustomerDocusignPage } from 'src/pages/admin-dashboard/customers/customer/docusign/CustomerDocusignPage'
import { CustomerInvoicePage } from 'src/pages/admin-dashboard/customers/customer/invoice/CustomerInvoicePage'
import { CustomerInvoiceDetailsPage } from 'src/pages/admin-dashboard/customers/customer/invoice/CustomerInvoiceDetailsPage'
import { CustomerMarketsPage } from 'src/pages/admin-dashboard/customers/customer/markets/CustomerMarketsPage'
import { CustomerOAuthPage } from 'src/pages/admin-dashboard/customers/customer/oauth/CustomerOAuthPage'
import { CustomerOnboardingTemplatesPage } from 'src/pages/admin-dashboard/customers/customer/onboarding-templates/CustomerOnboardingTemplatesPage'
import { CustomerPaymentsPage } from 'src/pages/admin-dashboard/customers/customer/payments/CustomerPaymentsPage'
import { CustomerProductsPage } from 'src/pages/admin-dashboard/customers/customer/products/CustomerProductsPage'
import { CustomerUsersPage } from 'src/pages/admin-dashboard/customers/customer/users/CustomerUsersPage'
import { CustomerWebhooksPage } from 'src/pages/admin-dashboard/customers/customer/webhooks/CustomerWebhooksPage'
import { CustomerInsurancePage } from 'src/pages/admin-dashboard/customers/customer/insurance/CustomerInsurancePage'
import { CustomerInsurancePolicyPage } from 'src/pages/admin-dashboard/customers/customer/insurance/CustomerInsurancePolicyPage'
import { InsuranceProductsPage } from 'src/pages/admin-dashboard/insurance-products/InsuranceProductsPage'
import { LmsCoursesPage } from 'src/pages/admin-dashboard/lms-courses/LmsCoursesPage'
import {
  ADMIN_DASHBOARD_CUSTOMER_TABS,
  ADMIN_DASHBOARD_GLOBAL_ITEMS,
} from 'src/pages/admin-dashboard/adminDashboardNav'
import type { AdminGlobalSlug, CustomerTabSlug } from 'src/pages/admin-dashboard/adminDashboardNav'
import { ADMIN_APPS, getAdminAppPermissions } from 'src/constants/adminApps'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { ADMIN_ROUTES } from './constants/routes'
import { CustomerAiReviewPage } from './pages/admin-dashboard/customers/customer/ai-review/CustomerAiReviewPage'
import { FlowEditorPage } from './pages/admin/flow-editor/FlowEditorPage'

/**
 * Customer tabs that have a real page. Everything else falls back to AppPlaceholderPage —
 * ENG-476 … ENG-485 each add one line here as their tab is built.
 */
const CUSTOMER_TAB_ELEMENTS: Partial<Record<CustomerTabSlug, React.ReactElement>> = {
  products: <CustomerProductsPage />,
  markets: <CustomerMarketsPage />,
  business: <CustomerBusinessPage />,
  docusign: <CustomerDocusignPage />,
  'onboarding-templates': <CustomerOnboardingTemplatesPage />,
  settings: <CustomerSettingsPage />,
  invoice: <CustomerInvoicePage />,
  insurance: <CustomerInsurancePage />,
  oauth: <CustomerOAuthPage />,
  payments: <CustomerPaymentsPage />,
  webhooks: <CustomerWebhooksPage />,
  users: <CustomerUsersPage />,
  'ai-review': <CustomerAiReviewPage />,
}

/**
 * Global (non-customer-scoped) pages that have a real page. Everything else falls back to
 * AppPlaceholderPage. ENG-474 adds the two catalog entries here as each page is built.
 */
const GLOBAL_ROUTE_ELEMENTS: Partial<Record<AdminGlobalSlug, React.ReactElement>> = {
  customers: <CustomersPage />,
  'insurance-products': <InsuranceProductsPage />,
  'lms-courses': <LmsCoursesPage />,
}

/**
 * Payments Ops pages that have a real page. Each page task adds its entry; anything missing
 * falls back to AppPlaceholderPage, the same mechanism the Admin Dashboard rail uses.
 */
const PAYMENTS_OPS_ROUTE_ELEMENTS: Partial<Record<PaymentsOpsSlug, React.ReactElement>> = {
  'payments-review': <PaymentsReviewPage />,
  'incoming-transactions': <IncomingTransactionsPage />,
  'outgoing-transactions': <OutgoingTransactionsPage />,
  refunds: <RefundsPage />,
  'bank-onboarding': <BankOnboardingPage />,
  'us-bank-transactions': <UsBankTransactionsPage />,
  wallets: <WalletsPage />,
  'fees-withdraw-reports': <FeesWithdrawReportsPage />,
  reconciliation: <ReconciliationPage />,
  killswitch: <KillswitchPage />,
}

// Route-level requirements (ENG-1207 part 2). Module constants so RequirePermission gets stable
// arrays. Each is the permission of the GET the screen cannot render without.
const DRIVER_APP_PERMISSIONS = getAdminAppPermissions('update-partner-driver')
const DOCUMENT_REVIEW_APP_PERMISSIONS = getAdminAppPermissions('internal-document-review')
const SCHEDULERS_APP_PERMISSIONS = getAdminAppPermissions('schedulers-management')
const PAYMENTS_OPS_APP_PERMISSIONS = getAdminAppPermissions('payments-ops')
const REMOTE_CONFIG_APP_PERMISSIONS = getAdminAppPermissions('remote-config')
const ADMIN_DASHBOARD_APP_PERMISSIONS = getAdminAppPermissions('admin-dashboard')
const CONTRACTOR_REVIEW_PERMISSIONS = [InternalPermission.DocumentReviewView]
const MC_REVIEW_PERMISSIONS = [InternalPermission.McDocumentReviewView]
// The wizard both lists onboardings (pre-fill, blocked partners) and creates one, so it needs
// view AND manage — two nested any-of guards.
const BANK_ONBOARDING_VIEW_PERMISSIONS = [InternalPermission.BankOnboardingView]
const NEW_BANK_ONBOARDING_PERMISSIONS = [InternalPermission.BankOnboardingManage]
// CustomerLayout loads GET /partners/:id for every customer tab.
const CUSTOMER_PERMISSIONS = [InternalPermission.PartnersView]
const INSURANCE_POLICY_PERMISSIONS = [InternalPermission.InsurancePoliciesView]
const INVOICE_DETAILS_PERMISSIONS = [InternalPermission.InvoicesView]
// The flow editor sits outside CustomerLayout and loads no partner.
const FLOW_EDITOR_PERMISSIONS = [InternalPermission.CollectDataTemplatesView]
const FLOW_EDITOR_NEW_PERMISSIONS = [InternalPermission.CollectDataTemplatesManage]
// The role editor loads GET /access/roles/:id. The Users Management page itself gates per tab, so
// only the editor routes need a guard here.
const INTERNAL_ROLES_VIEW_PERMISSIONS = [InternalPermission.InternalRolesView]

export const AppRoutes = () => {
  return (
    <BrowserRouter>
      <WorkosAuthProvider>
        <AdminQueryFlagsBootstrap>
          <Routes>
            {/* Outside the guard — the only screen a signed-out visitor may reach. */}
            <Route path={ADMIN_ROUTES.LOGIN_URL} element={<LoginPage />} />

            <Route element={<RequireInternalAuth />}>
              <Route element={<AdminShell />}>
                <Route path={ADMIN_ROUTES.LAUNCHER_URL} element={<LauncherPage />} />

                <Route element={<RequirePermission anyOf={DOCUMENT_REVIEW_APP_PERMISSIONS} />}>
                  <Route
                    path={ADMIN_ROUTES.INTERNAL_DOCUMENT_REVIEW_URL}
                    element={<DocumentReviewQueuePage />}
                  />
                  <Route
                    path={ADMIN_ROUTES.INTERNAL_DOCUMENT_REVIEW_CONTRACTOR_URL}
                    element={
                      <RequirePermission anyOf={CONTRACTOR_REVIEW_PERMISSIONS}>
                        <ContractorReviewPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path={ADMIN_ROUTES.INTERNAL_DOCUMENT_REVIEW_MC_URL}
                    element={
                      <RequirePermission anyOf={MC_REVIEW_PERMISSIONS}>
                        <McReviewPage />
                      </RequirePermission>
                    }
                  />
                </Route>
                <Route element={<RequirePermission anyOf={DRIVER_APP_PERMISSIONS} />}>
                  <Route
                    path={ADMIN_ROUTES.UPDATE_PARTNER_DRIVER_URL}
                    element={<PartnerDriverSearchPage />}
                  />
                  <Route
                    path={ADMIN_ROUTES.UPDATE_PARTNER_DRIVER_DRIVER_URL}
                    element={<PartnerDriverPage />}
                  />
                  <Route
                    path={ADMIN_ROUTES.UPDATE_PARTNER_DRIVER_STEPS_URL}
                    element={<PartnerDriverStepsPage />}
                  />
                </Route>
                <Route element={<RequirePermission anyOf={SCHEDULERS_APP_PERMISSIONS} />}>
                  <Route
                    path={ADMIN_ROUTES.SCHEDULERS_MANAGEMENT_URL}
                    element={<SchedulersManagementPage />}
                  />
                  {/* Declared before `:configId` so the static `/retries` segment wins. */}
                  <Route
                    path={ADMIN_ROUTES.SCHEDULERS_MANAGEMENT_ACTION_RETRIES_URL}
                    element={<ScheduledActionRetriesPage />}
                  />
                  <Route
                    path={ADMIN_ROUTES.SCHEDULERS_MANAGEMENT_ACTION_URL}
                    element={<ScheduledActionDetailPage />}
                  />
                </Route>

                <Route element={<RequirePermission anyOf={REMOTE_CONFIG_APP_PERMISSIONS} />}>
                  <Route path={ADMIN_ROUTES.REMOTE_CONFIG_URL} element={<RemoteConfigLayout />}>
                    <Route
                      index
                      element={<Navigate to={ADMIN_ROUTES.REMOTE_CONFIG_CLIENT_URL} replace />}
                    />
                    {REMOTE_CONFIG_ITEMS.map((item) => {
                      return (
                        <Route
                          key={item.app}
                          path={item.to}
                          // Keyed by app so switching apps starts a fresh search.
                          element={<RemoteConfigKeysPage key={item.app} app={item.app} />}
                        />
                      )
                    })}
                  </Route>
                </Route>

                <Route path={ADMIN_ROUTES.USERS_MANAGEMENT_URL} element={<UsersManagementPage />} />
                <Route element={<RequirePermission anyOf={INTERNAL_ROLES_VIEW_PERMISSIONS} />}>
                  {/* Declared before `:roleId`; the static `/new` segment wins regardless. */}
                  <Route
                    path={ADMIN_ROUTES.USERS_MANAGEMENT_NEW_ROLE_URL}
                    element={<InternalRolePage />}
                  />
                  <Route
                    path={ADMIN_ROUTES.USERS_MANAGEMENT_ROLE_URL}
                    element={<InternalRolePage />}
                  />
                </Route>

                <Route element={<RequirePermission anyOf={PAYMENTS_OPS_APP_PERMISSIONS} />}>
                  <Route path={ADMIN_ROUTES.PAYMENTS_OPS_URL} element={<PaymentsOpsLayout />}>
                    <Route index element={<FirstPermittedRedirect items={PAYMENTS_OPS_ITEMS} />} />
                    {PAYMENTS_OPS_ITEMS.map((item) => {
                      return (
                        <Route
                          key={item.to}
                          path={item.to}
                          element={
                            <RequirePermission anyOf={item.requiredAnyPermission}>
                              {PAYMENTS_OPS_ROUTE_ELEMENTS[item.slug] ?? (
                                <AppPlaceholderPage title={item.label} issueId={item.issueId} />
                              )}
                            </RequirePermission>
                          }
                        />
                      )
                    })}
                    <Route
                      path={ADMIN_ROUTES.PAYMENTS_OPS_NEW_BANK_ONBOARDING_URL}
                      element={
                        <RequirePermission anyOf={BANK_ONBOARDING_VIEW_PERMISSIONS}>
                          <RequirePermission anyOf={NEW_BANK_ONBOARDING_PERMISSIONS}>
                            <NewBankOnboardingPage />
                          </RequirePermission>
                        </RequirePermission>
                      }
                    />
                  </Route>
                </Route>

                <Route element={<RequirePermission anyOf={ADMIN_DASHBOARD_APP_PERMISSIONS} />}>
                  <Route path={ADMIN_ROUTES.ADMIN_DASHBOARD_URL} element={<AdminDashboardLayout />}>
                    <Route
                      index
                      element={<FirstPermittedRedirect items={ADMIN_DASHBOARD_GLOBAL_ITEMS} />}
                    />
                    {ADMIN_DASHBOARD_GLOBAL_ITEMS.map((item) => {
                      return (
                        <Route
                          key={item.to}
                          path={item.to}
                          element={
                            <RequirePermission anyOf={item.requiredAnyPermission}>
                              {GLOBAL_ROUTE_ELEMENTS[item.slug] ?? (
                                <AppPlaceholderPage title={item.label} issueId={item.issueId} />
                              )}
                            </RequirePermission>
                          }
                        />
                      )
                    })}

                    <Route
                      path={ADMIN_ROUTES.ADMIN_DASHBOARD_CUSTOMER_URL}
                      element={
                        <RequirePermission anyOf={CUSTOMER_PERMISSIONS}>
                          <CustomerLayout />
                        </RequirePermission>
                      }
                    >
                      <Route index element={<CustomerOverviewPage />} />
                      {ADMIN_DASHBOARD_CUSTOMER_TABS.map((tab) => {
                        return (
                          <Route
                            key={tab.slug}
                            path={tab.slug}
                            element={
                              <RequirePermission anyOf={tab.requiredAnyPermission}>
                                {CUSTOMER_TAB_ELEMENTS[tab.slug] ?? (
                                  <AppPlaceholderPage title={tab.label} issueId={tab.issueId} />
                                )}
                              </RequirePermission>
                            }
                          />
                        )
                      })}
                      <Route
                        path="insurance/:insurancePolicyPermanentId"
                        element={
                          <RequirePermission anyOf={INSURANCE_POLICY_PERMISSIONS}>
                            <CustomerInsurancePolicyPage />
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="invoice/:composedInvoiceId"
                        element={
                          <RequirePermission anyOf={INVOICE_DETAILS_PERMISSIONS}>
                            <CustomerInvoiceDetailsPage />
                          </RequirePermission>
                        }
                      />
                    </Route>
                    <Route
                      path={ADMIN_ROUTES.FLOW_EDITOR_NEW_URL}
                      element={
                        <RequirePermission anyOf={FLOW_EDITOR_NEW_PERMISSIONS}>
                          <FlowEditorPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path={ADMIN_ROUTES.FLOW_EDITOR_URL}
                      element={
                        <RequirePermission anyOf={FLOW_EDITOR_PERMISSIONS}>
                          <FlowEditorPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path={`${ADMIN_ROUTES.ADMIN_DASHBOARD_CUSTOMER_URL}/*`}
                      element={
                        <QueryPreservingNavigate to={ADMIN_ROUTES.ADMIN_DASHBOARD_CUSTOMER_URL} />
                      }
                    />
                  </Route>
                </Route>

                {ADMIN_APPS.filter((app) => {
                  return !app.isAvailable
                }).map((app) => {
                  return (
                    <Route
                      key={app.id}
                      path={app.path}
                      element={<AppPlaceholderPage title={app.title} issueId={app.issueId} />}
                    />
                  )
                })}

                <Route
                  path={ADMIN_ROUTES.LEGACY_FLOW_EDITOR_URL}
                  element={<QueryPreservingNavigate to={ADMIN_ROUTES.FLOW_EDITOR_URL} />}
                />

                <Route
                  path={ADMIN_ROUTES.AI_DOCUMENT_REVIEW_CONFIGS_URL}
                  element={
                    <QueryPreservingNavigate
                      to={ADMIN_ROUTES.ADMIN_DASHBOARD_CUSTOMER_AI_REVIEW_URL}
                    />
                  }
                />

                <Route path={ADMIN_ROUTES.NOT_FOUND_URL} element={<NotFoundPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Route>
          </Routes>
        </AdminQueryFlagsBootstrap>
      </WorkosAuthProvider>
    </BrowserRouter>
  )
}
